import type { ReactNode, RefObject } from 'react';

import AppStoreBadge from '@/assets/images/totp/google-authenticator-app-store.svg';
import GooglePlayBadge from '@/assets/images/totp/google-authenticator-google-play.svg';
import { type OtpInputHandle, Typography } from '@/shared/design-system';

import { TotpSetupPhase } from '../../totp-management-types';
import {
  Step,
  StepBody,
  StepList,
  StepMarker,
  StoreBadgeLink,
  StoreLinks,
} from './totp-setup.styled';
import { TotpSetupQr } from './totp-setup-qr';
import { TotpSetupVerification } from './totp-setup-verification';

const APP_STORE_URL = 'https://apps.apple.com/app/google-authenticator/id388497605';
const GOOGLE_PLAY_URL =
  'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2';

export type TotpSetupStepsProps = {
  setupUri: string;
  sharedSecret: string;
  code: string;
  copiedAnnouncement: string;
  error: string | null;
  phase: TotpSetupPhase;
  onCodeChange: (_code: string) => void;
  onCopy: () => void;
  onRetryActivation: () => void;
  otpRef: RefObject<OtpInputHandle | null>;
  backupStep?: ReactNode;
  codeLocked?: boolean;
};

export const TotpSetupSteps = ({
  setupUri,
  sharedSecret,
  code,
  copiedAnnouncement,
  error,
  phase,
  onCodeChange,
  onCopy,
  onRetryActivation,
  otpRef,
  backupStep,
  codeLocked,
}: TotpSetupStepsProps) => (
  <StepList>
    <Step>
      <StepMarker aria-hidden="true">1</StepMarker>
      <StepBody>
        <Typography variant="body2">
          Install an authenticator app. Google Authenticator is shown below, and any compatible
          authenticator app works.
        </Typography>
        <StoreLinks>
          <StoreBadgeLink
            href={APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="Download Google Authenticator for iOS"
          >
            <AppStoreBadge aria-hidden="true" focusable="false" />
          </StoreBadgeLink>
          <StoreBadgeLink
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="Download Google Authenticator for Android"
          >
            <GooglePlayBadge aria-hidden="true" focusable="false" />
          </StoreBadgeLink>
        </StoreLinks>
      </StepBody>
    </Step>

    <Step>
      <StepMarker aria-hidden="true">2</StepMarker>
      <StepBody>
        <TotpSetupQr
          setupUri={setupUri}
          sharedSecret={sharedSecret}
          copiedAnnouncement={copiedAnnouncement}
          onCopy={onCopy}
        />
      </StepBody>
    </Step>

    {backupStep && (
      <Step>
        <StepMarker aria-hidden="true">3</StepMarker>
        <StepBody>{backupStep}</StepBody>
      </Step>
    )}

    <Step>
      <StepMarker aria-hidden="true">{backupStep ? 4 : 3}</StepMarker>
      <StepBody>
        <TotpSetupVerification
          code={code}
          error={error}
          phase={phase}
          onCodeChange={onCodeChange}
          onRetryActivation={onRetryActivation}
          otpRef={otpRef}
          locked={codeLocked}
        />
      </StepBody>
    </Step>
  </StepList>
);
